import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";
import "./ChatbotPage.css";

const SESSION_STORAGE_KEY = "chatbot_session_id";
const GREETING = "Hi, I'm ThriftBot. Ask me about your products, platforms, and analytics.";

function makeSessionId() {
  return typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `session-${Date.now()}`;
}

// Cache image previews in sessionStorage so they survive page navigation
function saveImgCache(sid, previewUrl) {
  try {
    const list = JSON.parse(sessionStorage.getItem(`chat_imgs_${sid}`) || "[]");
    list.push(previewUrl);
    sessionStorage.setItem(`chat_imgs_${sid}`, JSON.stringify(list));
  } catch (_) {}
}
function getImgCache(sid) {
  try { return JSON.parse(sessionStorage.getItem(`chat_imgs_${sid}`) || "[]"); }
  catch (_) { return []; }
}

const ChatbotPage = () => {
  const { companyId } = useAuth();
  const [mode, setMode] = useState("general");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [toolsUsed, setToolsUsed] = useState([]);
  const [sessionId, setSessionId] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const fileInputRef = useRef(null);
  const [messages, setMessages] = useState([
    { role: "model", text: GREETING, createdAt: new Date().toISOString() },
  ]);

  // History sidebar
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [activeSessionTitle, setActiveSessionTitle] = useState(null);

  const listRef = useRef(null);
  const restoredRef = useRef(false);

  useEffect(() => {
    const existing = localStorage.getItem(SESSION_STORAGE_KEY);
    if (existing) {
      setSessionId(existing);
      restoredRef.current = true;
      return;
    }
    const id = makeSessionId();
    setSessionId(id);
    localStorage.setItem(SESSION_STORAGE_KEY, id);
  }, []);

  // Auto-load messages for a restored session (e.g. after page refresh)
  useEffect(() => {
    if (!restoredRef.current || !sessionId || !companyId) return;
    restoredRef.current = false;
    api.get(`/api/chat/${companyId}/sessions/${sessionId}`)
      .then((res) => {
        if (res?.data?.success && res.data.messages.length > 0) {
          const imgCache = getImgCache(sessionId);
          let imgIdx = 0;
          const loaded = res.data.messages.map((m) => {
            const msg = { role: m.role, text: m.message || "", hasImage: Boolean(m.has_image), createdAt: m.created_at };
            if (m.has_image && imgCache[imgIdx]) msg.imagePreview = imgCache[imgIdx++];
            return msg;
          });
          setMessages(loaded);
        }
      })
      .catch(() => {});
  }, [sessionId, companyId]);

  useEffect(() => {
    if (!listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, sending]);

  const fetchSessions = useCallback(async () => {
    if (!companyId) return;
    setLoadingSessions(true);
    try {
      const res = await api.get(`/api/chat/${companyId}/sessions`);
      if (res?.data?.success) setSessions(res.data.sessions || []);
    } catch (_) {}
    setLoadingSessions(false);
  }, [companyId]);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  const handleNewChat = useCallback(() => {
    const id = makeSessionId();
    setSessionId(id);
    localStorage.setItem(SESSION_STORAGE_KEY, id);
    setMessages([{ role: "model", text: GREETING, createdAt: new Date().toISOString() }]);
    setSelectedImage(null);
    setMessage("");
    setActiveSessionTitle(null);
  }, []);

  const loadSession = useCallback(async (session) => {
    try {
      const res = await api.get(`/api/chat/${companyId}/sessions/${session.id}`);
      if (res?.data?.success) {
        const imgCache = getImgCache(session.id);
        let imgIdx = 0;
        const loaded = res.data.messages.map((m) => {
          const msg = {
            role: m.role,
            text: m.message || "",
            hasImage: Boolean(m.has_image),
            createdAt: m.created_at,
          };
          if (m.has_image && imgCache[imgIdx]) {
            msg.imagePreview = imgCache[imgIdx++];
          }
          return msg;
        });
        setMessages(loaded.length > 0 ? loaded : [{ role: "model", text: GREETING, createdAt: new Date().toISOString() }]);
        setSessionId(session.id);
        localStorage.setItem(SESSION_STORAGE_KEY, session.id);
        setActiveSessionTitle(session.title);
        setSelectedImage(null);
        setMessage("");
      }
    } catch (_) {}
  }, [companyId]);

  const canSend = useMemo(
    () => Boolean(companyId) && Boolean(sessionId) &&
      (message.trim().length > 0 || Boolean(selectedImage)) && !sending,
    [companyId, sessionId, message, selectedImage, sending]
  );

  const handleImageSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      setSelectedImage({
        base64: e.target.result,
        mimeType: file.type || "image/jpeg",
        previewUrl: e.target.result,
        name: file.name,
      });
    };
    reader.readAsDataURL(file);
    event.target.value = "";
  };

  const handleRemoveImage = () => setSelectedImage(null);

  const handleSend = async () => {
    const trimmedMessage = message.trim();
    if (!trimmedMessage && !selectedImage) return;
    if (!companyId || !sessionId || sending) return;

    const imageToSend = selectedImage;
    const userMessage = {
      role: "user",
      text: trimmedMessage,
      imagePreview: selectedImage ? selectedImage.previewUrl : null,
      createdAt: new Date().toISOString(),
    };
    if (imageToSend?.previewUrl) saveImgCache(sessionId, imageToSend.previewUrl);
    setMessages((prev) => [...prev, userMessage]);
    setMessage("");
    setSelectedImage(null);
    setSending(true);

    try {
      const response = await api.post(`/api/chat/${companyId}`, {
        message: trimmedMessage,
        session_id: sessionId,
        mode,
        image: imageToSend ? imageToSend.base64 : undefined,
        mimeType: imageToSend ? imageToSend.mimeType : undefined,
      });

      if (response?.data?.success) {
        setMessages((prev) => [
          ...prev,
          { role: "model", text: response.data.reply || "No reply received.", createdAt: new Date().toISOString() },
        ]);
        setToolsUsed(Array.isArray(response.data.toolsUsed) ? response.data.toolsUsed : []);
        fetchSessions(); // refresh sidebar with updated session
      } else {
        setMessages((prev) => [
          ...prev,
          { role: "model", text: "I couldn't process that request right now. Please try again.", createdAt: new Date().toISOString() },
        ]);
      }
    } catch (error) {
      const serverMsg = error?.response?.data?.message;
      setMessages((prev) => [
        ...prev,
        { role: "model", text: serverMsg || "Chat service is unavailable right now. Please try again shortly.", createdAt: new Date().toISOString() },
      ]);
      setToolsUsed([]);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) { event.preventDefault(); handleSend(); }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  };

  const displayMessages = messages;

  return (
    <div className="chatbot-page">

      {/* â”€â”€ LEFT SIDEBAR â”€â”€ */}
      <aside className="chatbot-history-sidebar">
        <button type="button" className="chatbot-new-chat-btn" onClick={handleNewChat}>
          + New Chat
        </button>
        <div className="chatbot-history-label">Recent Chats</div>
        <div className="chatbot-session-list">
          {loadingSessions ? (
            <p className="chatbot-no-sessions">Loading...</p>
          ) : sessions.length === 0 ? (
            <p className="chatbot-no-sessions">No history yet.</p>
          ) : (
            sessions.map((s) => (
              <button
                type="button"
                key={s.id}
                className={`chatbot-session-item ${sessionId === s.id ? "active" : ""}`}
                onClick={() => loadSession(s)}
                title={s.title}
              >
                <span className="chatbot-session-item-title">{s.title}</span>
                <span className="chatbot-session-item-date">{formatDate(s.updated_at)}</span>
              </button>
            ))
          )}
        </div>
      </aside>

      {/* â”€â”€ MAIN CONTENT â”€â”€ */}
      <div className="chatbot-main">
        <div className="chatbot-page-header">
          <div>
            {activeSessionTitle ? (
              <>
                <h2>{activeSessionTitle}</h2>
                <p>
                  Resumed session &mdash;{" "}
                  <button type="button" className="chatbot-back-link" onClick={handleNewChat}>
                    Start New Chat
                  </button>
                </p>
              </>
            ) : (
              <>
                <h2>ThriftBot Assistant</h2>
                <p>Ask me about your products, platforms, and analytics.</p>
              </>
            )}
          </div>
          <div className="chatbot-controls">
            <label htmlFor="chatbot-mode">Mode</label>
            <select
              id="chatbot-mode"
              value={mode}
              onChange={(event) => setMode(event.target.value)}
              disabled={sending}
            >
              <option value="general">General</option>
              <option value="company">Company Specific</option>
            </select>
          </div>
        </div>

        <div className="chatbot-message-list" ref={listRef}>
          {displayMessages.map((item, index) => (
            <div key={`${item.role}-${index}`} className={`chatbot-message ${item.role}`}>
              <div className="chatbot-message-role">{item.role === "user" ? "You" : "ThriftBot"}</div>
              {item.imagePreview && (
                <img src={item.imagePreview} alt="uploaded" className="chatbot-message-image" />
              )}
              {item.hasImage && !item.imagePreview && (
                <div className="chatbot-message-image-placeholder">ðŸ“· Image was shared</div>
              )}
              {item.text && <div className="chatbot-message-text">{item.text}</div>}
            </div>
          ))}
          {sending && (
            <div className="chatbot-message model">
              <div className="chatbot-message-role">ThriftBot</div>
              <div className="chatbot-message-text">Thinking...</div>
            </div>
          )}
        </div>

        {toolsUsed.length > 0 && (
          <div className="chatbot-tools-used">
            <span>Tools used:</span>
            <div>{toolsUsed.join(", ")}</div>
          </div>
        )}

        <div className="chatbot-input-wrap">
            {selectedImage && (
              <div className="chatbot-image-preview-wrap">
                <img src={selectedImage.previewUrl} alt={selectedImage.name} className="chatbot-image-preview" />
                <button type="button" className="chatbot-image-remove" onClick={handleRemoveImage} title="Remove image">
                  &times;
                </button>
              </div>
            )}
            <div className="chatbot-input-row">
              <textarea
                placeholder="Ask: How many products do I have?"
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                onKeyDown={handleKeyDown}
                rows={3}
                disabled={sending}
              />
              <div className="chatbot-input-actions">
                <button
                  type="button"
                  className="chatbot-upload-btn"
                  onClick={() => fileInputRef.current && fileInputRef.current.click()}
                  disabled={sending}
                  title="Attach image"
                >
                  ðŸ“Ž
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={handleImageSelect}
                />
                <button type="button" onClick={handleSend} disabled={!canSend}>
                  Send
                </button>
              </div>
            </div>
        </div>
      </div>

    </div>
  );
};

export default ChatbotPage;